/**
 * Gössl Smart Search
 * Intercepts search queries and constructs smarter Shopify searches.
 * Handles German adjective forms, color terms, and product type matching.
 */
(function() {
  'use strict';

  // German color adjective stems → tag names
  var COLOR_MAP = {
    'blau': 'Blau', 'blaue': 'Blau', 'blaues': 'Blau', 'blauer': 'Blau', 'blauen': 'Blau', 'blauem': 'Blau',
    'grün': 'Grün', 'grüne': 'Grün', 'grünes': 'Grün', 'grüner': 'Grün', 'grünen': 'Grün', 'grünem': 'Grün',
    'rot': 'Rot', 'rote': 'Rot', 'rotes': 'Rot', 'roter': 'Rot', 'roten': 'Rot', 'rotem': 'Rot',
    'weiß': 'Weiß', 'weiße': 'Weiß', 'weißes': 'Weiß', 'weißer': 'Weiß', 'weißen': 'Weiß', 'weißem': 'Weiß',
    'weiss': 'Weiß', 'weisse': 'Weiß', 'weisses': 'Weiß', 'weisser': 'Weiß', 'weissen': 'Weiß',
    'schwarz': 'Schwarz', 'schwarze': 'Schwarz', 'schwarzes': 'Schwarz', 'schwarzer': 'Schwarz', 'schwarzen': 'Schwarz',
    'braun': 'Braun', 'braune': 'Braun', 'braunes': 'Braun', 'brauner': 'Braun', 'braunen': 'Braun',
    'grau': 'Grau', 'graue': 'Grau', 'graues': 'Grau', 'grauer': 'Grau', 'grauen': 'Grau',
    'rosa': 'Rosa', 'rosane': 'Rosa', 'rosanes': 'Rosa',
    'gold': 'Gold', 'goldene': 'Gold', 'goldenes': 'Gold', 'goldener': 'Gold', 'goldenen': 'Gold',
    'silber': 'Silber', 'silberne': 'Silber', 'silbernes': 'Silber',
    'beige': 'Beige', 'natur': 'Natur',
  };

  // Product type keywords that appear in titles/types
  var PRODUCT_TYPES = [
    'dirndl', 'dirndlkleid', 'dirndlbluse', 'bluse', 'blusen',
    'hemd', 'hemden', 'hose', 'hosen', 'jacke', 'jacken',
    'rock', 'röcke', 'kleid', 'kleider', 'strick', 'strickjacke',
    'weste', 'westen', 'mieder', 'mantel', 'mäntel',
    'lederhose', 'lederhosen', 'gilet', 'gilets',
    'tuch', 'tücher', 'schal', 'schals', 'tasche', 'taschen',
    'gürtel', 'schürze', 'schürzen', 'hut', 'hüte',
    'schuh', 'schuhe', 'krawatte', 'krawatten',
  ];

  // Article number patterns:
  // "23001 1100" or "23001 1100 2" (standard format)
  // "92306" (group_id — 5-digit standalone)
  // "23001_1100" (underscore variant)
  var ARTICLE_NO_PATTERN = /^(\d{4,5})[\s_]+(\d{3,4}(?:[\s_]+\d{1,2})?)$/;
  var GROUP_ID_PATTERN = /^\d{5}$/;

  function parseQuery(query) {
    var trimmed = query.trim();

    // Check for article number search (e.g., "23001 1100" or "23001 1100 2")
    if (ARTICLE_NO_PATTERN.test(trimmed)) {
      // Normalize underscores to spaces for Xano lookup
      return { isArticleNo: true, articleNo: trimmed.replace(/_/g, ' '), colors: [], productTerms: [], otherTerms: [] };
    }

    // Check for group_id search (e.g., "92306")
    if (GROUP_ID_PATTERN.test(trimmed)) {
      return { isGroupId: true, groupId: trimmed, colors: [], productTerms: [], otherTerms: [] };
    }

    var words = trimmed.toLowerCase().split(/\s+/);
    var colors = [];
    var productTerms = [];
    var otherTerms = [];

    for (var i = 0; i < words.length; i++) {
      var word = words[i];
      if (COLOR_MAP[word]) {
        colors.push(COLOR_MAP[word]);
      } else if (PRODUCT_TYPES.indexOf(word) !== -1) {
        productTerms.push(word);
      } else {
        // Check if it's a product type with adjective ending
        var found = false;
        for (var j = 0; j < PRODUCT_TYPES.length; j++) {
          if (word.indexOf(PRODUCT_TYPES[j]) === 0 || PRODUCT_TYPES[j].indexOf(word) === 0) {
            productTerms.push(PRODUCT_TYPES[j]);
            found = true;
            break;
          }
        }
        if (!found) otherTerms.push(word);
      }
    }

    return { colors: colors, productTerms: productTerms, otherTerms: otherTerms };
  }

  function buildSmartQuery(parsed, originalQuery) {
    var parts = [];

    // Add product terms
    if (parsed.productTerms.length > 0) {
      parts.push(parsed.productTerms.join(' '));
    }

    // Add other terms
    if (parsed.otherTerms.length > 0) {
      parts.push(parsed.otherTerms.join(' '));
    }

    // If no product/other terms extracted, use original minus color words
    if (parts.length === 0) {
      parts.push(originalQuery);
    }

    // Add color as product_type or tag filter
    if (parsed.colors.length > 0) {
      for (var i = 0; i < parsed.colors.length; i++) {
        parts.push('tag:' + parsed.colors[i]);
      }
    }

    return parts.join(' ');
  }

  // Intercept search form submissions
  document.addEventListener('submit', function(e) {
    var form = e.target;
    if (!form || form.getAttribute('role') !== 'search') return;

    var input = form.querySelector('input[name="q"]');
    if (!input || !input.value.trim()) return;

    var original = input.value.trim();
    var parsed = parseQuery(original);

    if (parsed.isArticleNo || parsed.isGroupId) {
      // Article number or group_id — search with tag prefix for better matching
      e.preventDefault();
      var q = parsed.isArticleNo ? 'Artikelnr:' + parsed.articleNo : parsed.groupId;
      var url = (form.action || '/search') + '?q=' + encodeURIComponent(q) + '&type=product';
      window.location.href = url;
    } else if (parsed.colors.length > 0) {
      // Rewrite with color tags
      e.preventDefault();
      var smartQuery = buildSmartQuery(parsed, original);
      var url = form.action || '/search';
      url += '?q=' + encodeURIComponent(smartQuery) + '&type=product';
      window.location.href = url;
    }
  });

  // Also intercept predictive search fetches
  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function(url, opts) {
      if (typeof url === 'string' && url.includes('/search/suggest')) {
        try {
          var parsed2 = new URL(url, window.location.origin);
          var q = parsed2.searchParams.get('q');
          if (q) {
            var p = parseQuery(q);
            if (p.colors.length > 0) {
              var smart = buildSmartQuery(p, q);
              parsed2.searchParams.set('q', smart);
              url = parsed2.toString();
            }
          }
        } catch(e) {}
      }
      return origFetch.call(this, url, opts);
    };
  }
})();
